import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

// Patient drawers are URL-driven everywhere else in the app (":role/search/
// patient/:mrn" etc.), but the merge screens can't use that: those routes are
// flat siblings, so navigating to one unmounts the merge screen and throws
// away the whole in-progress merge — the customer-merge result, the detected
// duplicate patient groups and the inline patient-merge selections. Here the
// drawer is driven by local state and rendered on top of the merge screen
// instead, which keeps the screen mounted. Links inside the drawer that would
// navigate away open in a new tab, the same way the conflicting-appointment
// links on the duplicate-group card do (AC38).
const useMergeScreenPatientDrawer = () => {
  const { role } = useParams();
  const [patientMrn, setPatientMrn] = useState<string | null>(null);

  const openPatientDrawer = useCallback((mrn: string) => {
    setPatientMrn(mrn);
  }, []);

  const handleClose = useCallback(() => setPatientMrn(null), []);

  const handleCustomerClick = useCallback(
    (customerId: string) => {
      window.open(`/${role}/search/customer/${customerId}`, "_blank");
    },
    [role],
  );

  const handleAppointmentClick = useCallback(
    (appointmentId: string) => {
      window.open(`/${role}/appointment/${appointmentId}`, "_blank");
    },
    [role],
  );

  const patientDrawerProps = useMemo(
    () => ({
      patientMrn: patientMrn ?? undefined,
      isOpen: Boolean(patientMrn),
      handleClose,
      handleCustomerClick,
      handleAppointmentClick,
      isMergeActionShown: false,
      // Opening an already-merged patient swaps in its merge target without
      // touching the URL (the MRN isn't in the URL in this mode).
      onMergeRedirect: setPatientMrn,
    }),
    [patientMrn, handleClose, handleCustomerClick, handleAppointmentClick],
  );

  return {
    isPatientDrawerOpen: Boolean(patientMrn),
    openPatientDrawer,
    patientDrawerProps,
  };
};

export default useMergeScreenPatientDrawer;
